import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';

const LoginModal = () => {
	const navigate = useNavigate();
	const { loginModalOpen, closeLoginModal, login } = useContext(AuthContext);
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	if (!loginModalOpen) return null;

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setIsLoading(true);

		try {
			const result = await login({ username, password });
			
			if (result.success) {
				// Login successful
				closeLoginModal();
				
				// Navigate based on role
				if (result.user.role === 'admin') {
					navigate('/admin/dashboard');
				} else if (result.user.role === 'staff') {
					// Staff stays on home page (can access admin panel manually)
					navigate('/');
				} else {
					navigate('/');
				}
			} else {
				setError(result.message || 'Login failed. Please try again.');
			}
		} catch (err) {
			setError('An unexpected error occurred. Please try again.');
			console.error('Login error:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSignupClick = () => {
		closeLoginModal();
		navigate('/signup');
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="bg-white max-w-md w-full rounded-lg shadow-lg overflow-hidden">
				{/* Close button */}
				<div className="flex justify-end p-4">
					<button
						onClick={closeLoginModal}
						className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
						aria-label="Close"
					>
						×
					</button>
				</div>

				<div className="px-6 py-4 text-center">
					{/* Logo */}
					<div className="mb-4 flex justify-center">
						<img src="/images/casa-gatchalian-logo.jpg" alt="Casa Gatchalian" className="h-12 rounded-full" />
					</div>

					{/* Heading */}
					<h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back!</h2>
					<p className="text-sm text-gray-500 mb-6">Sign into your account</p>

					{/* Error Message */}
					{error && (
						<div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
							{error}
						</div>
					)}

					{/* Form */}
					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<input
								type="text"
								placeholder="Username or Email"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								required
								autoComplete="username"
								className="w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#0f4c81] focus:border-[#0f4c81] text-sm"
							/>
						</div>
						<div>
							<input
								type="password"
								placeholder="Password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								autoComplete="current-password"
								className="w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#0f4c81] focus:border-[#0f4c81] text-sm"
							/>
						</div>

						{/* Forgot password link */}
						<div className="text-right">
							<button 
								type="button"
								onClick={() => {
									closeLoginModal();
									navigate('/forgot-password');
								}}
								className="text-xs text-red-500 hover:underline"
							>
								Forgot your password?
							</button>
						</div>

						{/* Log in button */}
						<button
							type="submit"
							disabled={isLoading}
							className="w-full py-3 bg-gray-800 text-white rounded-full font-semibold text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
						>
							{isLoading ? 'Logging in...' : 'Log in'}
						</button>
					</form>

					{/* Terms and conditions */}
					<div className="mt-6 text-xs text-gray-600">
						By continuing, you agree to our updated <a href="#" className="text-[#0f4c81] hover:underline font-medium">Terms & Conditions</a> and <a href="#" className="text-[#0f4c81] hover:underline font-medium">Privacy Policy</a>
					</div>

					{/* Footer */}
					<div className="mt-4 text-sm">
						<span className="text-gray-500">Don't have an account? </span>
						<button type="button" onClick={handleSignupClick} className="text-red-500 hover:underline font-medium">Sign up here</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LoginModal;
